import { useState } from "react";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { useFinancialState } from "@/context/StateContext";
import { uid } from "@/lib/finance";
import { PageHeader, SectionCard, EmptyState, AddButton, StatusPill, KpiCard } from "@/components/shared";
import { FormModal, TextField, SelectField, TextArea } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const STATUSES = [{ value: "protected", label: "Protected" }, { value: "review", label: "Needs review" }, { value: "gap", label: "Gap" }];

export default function Protection() {
  const { state, update, loading } = useFinancialState();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  if (loading || !state) return <Skeleton className="h-96 rounded-[14px]" />;

  const items = state.protection || [];
  const openNew = () => { setForm({ id: uid(), name: "", type: "Insurance", status: "review", notes: "" }); setOpen(true); };
  const save = () => {
    if (!form.name) return toast.error("Name is required");
    update((prev) => {
      const exists = (prev.protection || []).some((x) => x.id === form.id);
      return { ...prev, protection: exists ? prev.protection.map((x) => (x.id === form.id ? form : x)) : [...(prev.protection || []), form] };
    }, `Saved protection: ${form.name}`);
    setOpen(false); toast.success("Protection item saved");
  };
  const remove = (id) => update((prev) => ({ ...prev, protection: prev.protection.filter((x) => x.id !== id) }), "Deleted protection item");

  const counts = { protected: items.filter((i) => i.status === "protected").length, review: items.filter((i) => i.status === "review").length, gap: items.filter((i) => i.status === "gap").length };

  return (
    <div>
      <PageHeader title="Protection" subtitle="Insurance, estate, and identity safeguards — know where you stand." testid="protection-header"
        actions={<AddButton onClick={openNew} testid="add-protection-button"><Plus className="h-4 w-4 mr-1.5" />Add item</AddButton>} />

      <div className="grid grid-cols-3 gap-4 mb-6">
        <KpiCard label="Protected" value={counts.protected} testid="prot-protected" />
        <KpiCard label="Needs review" value={counts.review} testid="prot-review" />
        <KpiCard label="Gaps" value={counts.gap} testid="prot-gap" />
      </div>

      {items.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((i) => (
            <SectionCard key={i.id} testid={`protection-card-${i.id}`}>
              <div className="flex items-start justify-between">
                <div><h3 className="font-serif-display text-lg text-[var(--text)]">{i.name}</h3><div className="text-[11px] text-[var(--text-muted)] mt-0.5">{i.type}</div></div>
                <StatusPill status={i.status} />
              </div>
              {i.notes && <p className="text-sm text-[var(--text-muted)] mt-3">{i.notes}</p>}
              <div className="flex justify-end mt-3">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setForm({ ...i }); setOpen(true); }} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-[var(--danger)]" onClick={() => remove(i.id)} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </SectionCard>
          ))}
        </div>
      ) : <SectionCard><EmptyState icon={ShieldCheck} title="No protection items" message="Track your insurance, estate plans, and identity safeguards." action={<AddButton onClick={openNew}><Plus className="h-4 w-4 mr-1.5" />Add item</AddButton>} /></SectionCard>}

      {form && (
        <FormModal open={open} onOpenChange={setOpen} title={items.some((x) => x.id === form.id) ? "Edit protection item" : "Add protection item"} onSubmit={save} testid="protection-modal">
          <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} full testid="protection-name-input" />
          <TextField label="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="Insurance, Estate, Identity" />
          <SelectField label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={STATUSES} testid="protection-status-select" />
          <TextArea label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} full rows={3} />
        </FormModal>
      )}
    </div>
  );
}
